const GUEST_DEVICE_STORAGE_KEY = 'thesis_guest_device_id';

const cleanValue = (value) => String(value || '').trim();

export const parseAuthorName = (fullName) => {
  const cleanName = cleanValue(fullName);
  if (!cleanName) return { firstName: '', lastName: '' };

  if (cleanName.includes('||')) {
    const [firstName, lastName] = cleanName.split('||');
    return {
      firstName: cleanValue(firstName).replace(/,\s*$/, ''),
      lastName: cleanValue(lastName).replace(/,\s*$/, '')
    };
  }

  if (cleanName.includes(',')) {
    const [lastName, firstName] = cleanName.split(',').map(cleanValue);
    return { firstName, lastName };
  }

  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: '', lastName: parts[0] };

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1]
  };
};

const normalizeAuthor = (author) => {
  if (!author) return null;
  if (typeof author === 'string') return parseAuthorName(author);

  if (author.name) {
    const parsed = parseAuthorName(author.name);
    return {
      firstName: cleanValue(author.firstName || parsed.firstName),
      lastName: cleanValue(author.lastName || parsed.lastName)
    };
  }

  return {
    firstName: cleanValue(author.firstName),
    lastName: cleanValue(author.lastName)
  };
};

export const getNormalizedAuthors = (authors = []) => (
  (Array.isArray(authors) ? authors : [])
    .map(normalizeAuthor)
    .filter((author) => author && (author.firstName || author.lastName))
);

const toInitials = (firstName = '') => (
  cleanValue(firstName)
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join(' ')
);

export const formatAuthorsForDisplay = (authors = []) => (
  getNormalizedAuthors(authors)
    .map(({ firstName, lastName }) => (
      lastName ? `${lastName}, ${firstName}`.trim() : firstName
    ))
    .filter(Boolean)
    .join('; ')
);

const formatApaAuthors = (authors = []) => {
  const names = getNormalizedAuthors(authors).map(({ firstName, lastName }) => {
    const initials = toInitials(firstName);
    if (!lastName) return initials || 'Unknown Author';
    return initials ? `${lastName}, ${initials}` : lastName;
  });

  if (names.length === 0) return 'Unknown Author';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, & ${names[names.length - 1]}`;
};

const buildDepartmentLine = (thesis, getDegreeName, getMajorName) => {
  const parts = [
    cleanValue(thesis?.department) || 'Unknown Department',
    thesis?.degree_id ? cleanValue(getDegreeName?.(thesis.degree_id)) : '',
    thesis?.major_id ? cleanValue(getMajorName?.(thesis.major_id)) : ''
  ].filter(Boolean);

  return parts.join(', ');
};

export const formatCitation = (
  thesis,
  { getDegreeName, getMajorName } = {}
) => {
  const year = cleanValue(thesis?.year) || 'N.D.';
  const title = cleanValue(thesis?.title) || 'Untitled';
  const departmentLine = buildDepartmentLine(thesis, getDegreeName, getMajorName);

  return `${formatApaAuthors(thesis?.authors)} (${year}). ${title}. ${departmentLine}.`;
};

const generateGuestDeviceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getCitationActor = (user) => {
  if (user?.user_id) return { userId: user.user_id };

  if (typeof window === 'undefined') return { guestDeviceId: generateGuestDeviceId() };

  let guestDeviceId = window.localStorage.getItem(GUEST_DEVICE_STORAGE_KEY);
  if (!guestDeviceId) {
    guestDeviceId = generateGuestDeviceId();
    window.localStorage.setItem(GUEST_DEVICE_STORAGE_KEY, guestDeviceId);
  }

  return { guestDeviceId };
};

export const copyTextToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};
